<template>
  <dashboard-card :fixedHeight="false" :hideDescription="mode === 'lite'">
    <div slot="title">
      <div class="row">
        <div class="col-md-6">
          <h3 class="pull-left">&nbsp;Profiles Filter</h3>
        </div>
        <div>
          <identifiers-filter
            ref="identifier"
            @filter="handleOnFilter"
            class="pull-right"
            style="margin-right: 10px"
            v-if="mode === 'lite'"
          />
        </div>
        <div class="col-md-6" v-if="mode === 'pro'">
          <h3 class="pull-right" style="margin-right: 10px">
            <i v-if="loading" class="fa fa-spinner fa-spin"></i>
            <span v-else>
              Profiles: <strong>{{ formatNumber(results.total, 0, ',') }}</strong>
            </span>
          </h3>
        </div>
      </div>
    </div>

    <div slot="description">Select the fields you would like to use to filter the profiles</div>

    <div slot="content">
      <div v-loading="loading" element-loading-text="Loading Profile Filters...">
        <el-collapse
          v-if="mode === 'pro'"
          style="border-top: none"
          accordion
          v-model="activeFilters"
        >
          <el-collapse-item name="demographics">
            <template slot="title">
              <span class="collapse-title">Demographics</span>
            </template>
            <demographics-filter
              ref="demos"
              :filters="demographics"
              :total="results.total"
              @filter="handleOnFilter"
            />
          </el-collapse-item>

          <el-collapse-item name="campaignsAndSurveys">
            <template slot="title">
              <span class="collapse-title">Campaigns & Surveys</span>
            </template>
            <campaigns-filter ref="campaign" :campaigns="campaigns" @filter="handleOnFilter" />
          </el-collapse-item>

          <el-collapse-item name="optins">
            <template slot="title">
              <span class="collapse-title">Opt-in Status</span>
            </template>
            <optins-filter ref="optin" @filter="handleOnFilter" />
          </el-collapse-item>

          <el-collapse-item name="profileSearch">
            <template slot="title">
              <span class="collapse-title">Profile Search</span>
            </template>
            <identifiers-filter ref="identifier" @filter="handleOnFilter" />
          </el-collapse-item>

          <el-collapse-item name="randomSample">
            <template slot="title">
              <span class="collapse-title">Random Sample</span>
            </template>
            <el-checkbox v-model="computedEnableRandomSample">Enable random sampling</el-checkbox>
            <random-sample-filter
              ref="randomsample"
              :sample-size="sampleSize"
              :available-profiles="results.filteredTotal"
              @change-sample-size="handleChangeSampleSize"
              @filter="handleOnFilter"
              v-if="sampleSize > 0"
            />
          </el-collapse-item>
        </el-collapse>

        <div class="row filtered-count" v-if="mode === 'pro'">
          <div class="col-md-12 text-right">
            <i v-if="searching" class="fa fa-spinner fa-spin"></i>
            <span v-else
              >Profiles: <strong>{{ formatNumber(results.filteredTotal, 0, ',') }}</strong></span
            >
          </div>
        </div>

        <profiles-data-export
          v-if="mode === 'pro'"
          @download="handleDownloadQuery"
          @display-profiles="handleDisplayProfiles"
          @clear-filters="handleClearFilters"
          @save-query="handleSaveQuery"
          @apply-query="handleApplySavedQuery"
          @delete-query="$emit('delete-query', $event)"
          :canDownload="identifierSelected"
          :profilesVisible="displayProfiles"
          :saved-queries="savedQueries"
          :loading-saved-queries="loadingSavedQueries"
          :v-if="mode === 'pro'"
          id="profile-data-export"
        />

        <transition name="slide">
          <profiles-browser
            v-if="!proMode || displayProfiles"
            :profiles="results.profiles"
            :filtered-total="results.filteredTotal"
            :loading="searching"
            :loading-profiles="loadingProfiles"
            @load-more-profiles="$emit('load-more-profiles')"
          />
        </transition>
      </div>
    </div>
  </dashboard-card>
</template>

<script>
import DashboardCard from '@/components/profiles/DashboardSectionCard';
import DemographicsFilter from '@/components/profiles/filters/DemographicsFilter';
import CampaignsFilter from '@/components/profiles/filters/CampaignsFilter';
import OptinsFilter from '@/components/profiles/filters/OptinsFilter';
import RandomSampleFilter from '@/components/profiles/filters/RandomSampleFilter';
import ProfilesDataExport from '@/components/profiles/ProfilesDataExport';
import ProfilesBrowser from '@/components/profiles/ProfilesBrowser';
import IdentifiersFilter from '@/components/profiles/filters/IdentifiersFilter';

import { formatNumber } from 'accounting';
import { get, omitBy, isEmpty, isNumber } from 'lodash';

const DEFAULT_SAMPLE_SIZE = 250;

export default {
  name: 'ProfileDataFilter',
  components: {
    DashboardCard,
    DemographicsFilter,
    CampaignsFilter,
    OptinsFilter,
    RandomSampleFilter,
    ProfilesBrowser,
    ProfilesDataExport,
    IdentifiersFilter,
  },
  data: () => ({
    activeFilters: ['demographics'],
    profiles: [],
    query: { 'meta.opt_out': ['email', 'sms', 'whatsapp', 'telegram'] },
    campaigns: [],
    sampleSize: 0,
    displayProfiles: false,
  }),
  computed: {
    computedEnableRandomSample: {
      get() {
        return this.sampleSize > 0;
      },

      set(value) {
        this.sampleSize = value ? DEFAULT_SAMPLE_SIZE : 0;
      },
    },
    demographics() {
      return get(this.filters, 'demographics');
    },
    identifierSelected() {
      return 'identifiers' in this.query;
    },
    proMode() {
      return this.mode === 'pro';
    },
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    results: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    loadingProfiles: {
      type: Boolean,
      required: true,
    },
    loadingSavedQueries: {
      type: Boolean,
      required: true,
    },
    searching: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String,
      default: 'lite',
    },
    savedQueries: {
      type: Array,
      required: true,
    },
  },
  watch: {
    filters(values) {
      this.campaigns = get(values, 'campaigns');
    },
  },
  methods: {
    get,
    formatNumber,
    handleDisplayProfiles() {
      this.displayProfiles = !this.displayProfiles;
      this.$emit('display-profiles');
    },
    handleOnFilter([path, value]) {
      this.query[path] = value;
      this.query = omitBy(this.query, v => (isNumber(v) ? false : isEmpty(v)));
      this.$emit('filter', this.query);
    },
    handleClearFilters() {
      ['demos', 'optin', 'identifier', 'randomsample'].forEach(ref => {
        if (this.$refs[ref] && typeof this.$refs[ref].reset === 'function') {
          this.$refs[ref].reset();
        }
      });

      this.campaigns = this.campaigns.map(c => ({
        ...c,
        selected: false,
      }));
    },
    handleSaveQuery(title) {
      this.$emit('save-query', { title, query: this.query });
    },
    handleDownloadQuery(title) {
      this.$emit('download', { title, query: this.query, sampleSize: this.sampleSize });
    },
    handleApplySavedQuery(query) {
      this.query = query;
      this.safeApplyFilter('demos', query);
      this.safeApplyFilter('optin', query);
      this.safeApplyFilter('randomsample', query);
      this.applyCampaignQuery(query);
      this.$emit('filter', this.query);
    },
    handleChangeSampleSize(sampleSize) {
      this.sampleSize = sampleSize;
    },
    applyCampaignQuery(query) {
      const savedCampaigns = get(query, 'meta.campaign', []).map(c => c.toString());

      this.campaigns = this.campaigns.map(c => {
        const campaign = c;
        if (savedCampaigns.includes(campaign.id)) {
          campaign.selected = true;
        }
        return campaign;
      });
    },
    safeApplyFilter(refName, query) {
      const component = this.$refs[refName];
      if (component && typeof component.apply === 'function') {
        component.apply(query);
      }
    },
  },
};
</script>

<style scoped>
#profile-data-export {
  margin: 40px 0px;
}

.slide-enter-active {
  -moz-transition-duration: 0.3s;
  -webkit-transition-duration: 0.3s;
  -o-transition-duration: 0.3s;
  transition-duration: 0.3s;
  -moz-transition-timing-function: ease-in;
  -webkit-transition-timing-function: ease-in;
  -o-transition-timing-function: ease-in;
  transition-timing-function: ease-in;
}

.slide-leave-active {
  -moz-transition-duration: 0.3s;
  -webkit-transition-duration: 0.3s;
  -o-transition-duration: 0.3s;
  transition-duration: 0.3s;
  -moz-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  -webkit-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  -o-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
}

.slide-enter-to,
.slide-leave {
  max-height: 100px;
  overflow: hidden;
}

.slide-enter,
.slide-leave-to {
  overflow: hidden;
  max-height: 0;
}

.filtered-count {
  margin-top: 20px;
  font-size: 16px;
  text-decoration: underline;
  margin-right: 0px;
}
.collapse-title {
  color: #676a6c;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// ProfilesDataFilter.vue?71d5ba0a